// Deps
import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {chunk, cloneDeep, isEqual, uniq} from 'lodash';
import {Loader} from '@flexe/ui-components';
import {useCookies} from 'react-cookie';
import {usePrevious, valueIsNotUndefined} from '../../../shared/ReactHelpers';
import useSearchParams from '../../../shared/useSearchParams';

// Services
import BatchWavingService from '../../../shared/services/BatchWavingService';
import OutboundShipmentService from '../../../shared/services/OutboundShipmentService';
import WarehouseService from '../../../shared/services/WarehouseService';
import PickConsolidationService from '../../../shared/services/PickConsolidationService';

// Interfaces
import {Reservation, SortOrder, Warehouse} from '../../../shared/CommonInterfaces';
import * as BatchWaving from '../../../warehouse/ecommerce-batches/BatchInterfaces';
import {
  FilterKeys,
  GetShipmentsParams,
  Location,
  ReservationDisplayInfo,
  ReservationDisplayInfos,
  SelectedShipment,
  Shipment,
  SortByColumns
} from '../ShipmentInterfaces';
import {filterParamsFromURLSearchParams} from '../ShipmentSearchParams';

// Components
import SelectorWithCheckbox from '../../../shared/SelectorWithCheckbox';
import WavingModal, {OnWaveClickOptions} from '../waving/WavingModal';
import WavingSuccessModal from '../waving/WavingSuccessModal';
import LpnService from '../../../shared/services/LpnService';
import {LpnSearchDetails, LpnSearchRequest} from '../../../lpns/LpnsInterfaces';
import CheckboxDropdown from '../../../shared/CheckboxDropdown';
import AlertWavingIssues from '../../batch-waving/AlertWavingIssues';
import {FreightShipMode, ShipMode} from '../../../shared/constants';
import WavingControls from '../waving/WavingControls';
import LpnContentsModal from './LpnContentsModal';
import SortBy from './SortBy';
import {ColumnNames, SortState} from './ShipmentTableHeader';
import ShipmentTable from './ShipmentTable';
import ShipmentFilter from './ShipmentFilter';
import ShipBy from './ShipBy';
import PaginationButtons from './PaginationButtons';
import ModeFilter from './ModeFilter';

interface SelectedStatusFilters {
  [key: string]: boolean;
}

interface SimpleShipmentStatusMap {
  [key: string]: string;
}

interface ShipmentIndexProps {
  authenticityToken: string;
  currentWarehouse: Warehouse;
  availableWarehouses: Warehouse[];
  isFreightTrailerLoadingEnabled: boolean;
  isLpnEnabled: boolean;
  isDistributionByLotEnabled: boolean;
  reservationDisplayInfos: ReservationDisplayInfos;
}

const FF_FILTER_STAGING_LOCATIONS = 'filter-staging-locations-dropdown';
const FF_FREIGHT_LOAD_GROUP_UI = 'oms-freight-load-group-ui';

const getStatusFilters = (statusFilters: SelectedStatusFilters): string[] =>
  Object.entries(statusFilters)
    .filter(([filterName, filterValue]) => filterName !== 'all' && filterValue)
    .map(([filterName, _]) => filterName);

function ShipmentIndex(props: ShipmentIndexProps) {
  const {
    authenticityToken,
    availableWarehouses,
    currentWarehouse,
    isDistributionByLotEnabled,
    isFreightTrailerLoadingEnabled,
    reservationDisplayInfos
  } = props;
  const batchWavingService = new BatchWavingService(authenticityToken);
  const outboundShipmentService = new OutboundShipmentService(authenticityToken);
  const warehouseService = new WarehouseService(authenticityToken);
  const pickConsolidationService = new PickConsolidationService(authenticityToken);
  const lpnService = new LpnService(authenticityToken);

  const ALL_RESERVATIONS_ID_VALUE = -1;
  const PAGE_SIZE = 50;

  const NO_SELECTED_STATUS_FILTERS = {
    all: false,
    new: false,
    confirmed: false,
    packed: false,
    completed: false,
    cancelled: false,
    attention_required: false,
    voided: false
  };

  const DEFAULT_SELECTED_STATUS_FILTERS = {
    all: false,
    new: true,
    confirmed: true,
    packed: true,
    completed: false,
    cancelled: false,
    attention_required: false,
    voided: false
  };

  const ALL_SELECTED_STATUS_FILTERS = {
    all: true,
    new: true,
    confirmed: true,
    packed: true,
    completed: true,
    cancelled: true,
    attention_required: true,
    voided: true
  };

  const SIMPLE_SHIPMENT_STATUS_MAP: SimpleShipmentStatusMap = {
    new: 'Not Started',
    confirmed: 'In Progress',
    packed: 'Packed',
    completed: 'Shipped',
    cancelled: 'Cancelled',
    attention_required: 'Attention Required',
    voided: 'Voided'
  };

  // TODO: THESE WILL NEED TO CHANGE WHEN NEW STATUS MAP GETS IMPLEMENTED
  // grey: not started --> new
  // purple: in progress --> confirmed
  // green: shipped --> completed
  // red: cancelled --> cancelled
  // red: attention required --> attention_required
  // red: voided --> voided
  // use context-specific states as keys, status-dot specific states as values
  const STATUS_TO_DOT_STYLE_MAP = {
    new: 'neutral',
    confirmed: 'support',
    packed: 'inform',
    completed: 'valid',
    cancelled: 'invalid',
    attention_required: 'invalid',
    voided: 'invalid'
  };

  const [errors, setErrors] = useState<string[]>([]);
  const [reloadShipments, setReloadShipments] = useState(false);
  const [continuationTokens, setContinuationTokens] = useState({1: null});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState(true);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [selectedShipments, setSelectedShipments] = useState<SelectedShipment[]>([]);
  const [shipByEnd, setShipByEnd] = useState<Date>(null);
  const [shipByStart, setShipByStart] = useState<Date>(null);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [shipmentsCount, setShipmentsCount] = useState<number>(0);
  const [showWavingModal, setShowWavingModal] = useState(false);
  const [showWavingSuccessModal, setShowWavingSuccessModal] = useState(false);
  const [selectedStatusFilters, setSelectedStatusFilters] = useState<SelectedStatusFilters>(
    DEFAULT_SELECTED_STATUS_FILTERS
  );
  const [lpns, setLpns] = useState<LpnSearchDetails[]>(null);
  const [showLpnContentsModal, setShowLpnContentsModal] = useState(false);
  const [shipmentForLPN, setShipmentForLPN] = useState<Shipment>(null);
  const [filterStagingLocations, setFilterStagingLocations] = useState(false);
  const [isFreightLoadGroupUIEnabled, setIsFreightLoadGroupUIEnabled] = useState(false);
  const [reloadAlerts, setReloadAlerts] = useState(false);
  const [allowedFilterKeys, setAllowedFilterKeys] = useState<FilterKeys[]>([]);
  const {searchParams, setSearchParam, setSearchParams, removeSearchParam} = useSearchParams();

  const [cookies] = useCookies(['selected_warehouse_id']);

  const selectedWarehouse = useMemo(() => {
    if (cookies.selected_warehouse_id) {
      return availableWarehouses.find(
        (warehouse: Warehouse) => warehouse.id === parseInt(cookies.selected_warehouse_id, 10)
      );
    }

    return currentWarehouse;
  }, [cookies, availableWarehouses]);

  const prevWh = usePrevious(selectedWarehouse);

  useEffect(() => {
    getReservations();
    loadFilterStagingLocationsFlag();
    if (valueIsNotUndefined(prevWh)) {
      setReloadShipments(!reloadShipments);
    }
    loadFreightLoadGroupUIEnabledFlag();
    getFilterKeys();
  }, [selectedWarehouse]);

  useEffect(() => {
    getFilterKeys();
  }, [isFreightLoadGroupUIEnabled]);

  useEffect(() => {
    setupInitialStateFromSearchParams(searchParams);
  }, []);

  const setupInitialStateFromSearchParams = (params: URLSearchParams) => {
    if (params.size > 0) {
      const statusFromSearchParams = params.getAll('status');
      const today = new Date();
      const tOffsetDays = (offset: number) => {
        const d = new Date(today);
        d.setDate(d.getDate() + offset);
        return d;
      };
      const yesterday = tOffsetDays(-1);
      const tomorrow = tOffsetDays(1);
      const tPlusTwoDays = tOffsetDays(2);
      const tPlusThreeDays = tOffsetDays(3);

      if (statusFromSearchParams.length > 0) {
        const newSelectedStatusFilters: SelectedStatusFilters = cloneDeep(NO_SELECTED_STATUS_FILTERS);
        statusFromSearchParams.forEach((status) => {
          if (status in newSelectedStatusFilters) {
            newSelectedStatusFilters[status] = true;
          }
        });
        setSelectedStatusFilters(newSelectedStatusFilters);
      } else {
        setSearchParam('status', getStatusFilters(DEFAULT_SELECTED_STATUS_FILTERS));
      }

      const shipByStartParam = params.get('ship_by_start');
      const shipByEndParam = params.get('ship_by_end');

      switch (shipByStartParam) {
        case 'anytime':
        case 'pastDue':
          setShipByStart(null);
          break;
        case 'today':
        case 'todayAndTomorrow':
        case 'todayPlusTwo':
        case 'todayPlusThree':
          setShipByStart(today);
          break;
        default:
          setShipByStart(null);
          break;
      }

      switch (shipByEndParam) {
        case 'anytime':
          setShipByEnd(null);
          break;
        case 'pastDue':
          setShipByEnd(yesterday);
          break;
        case 'today':
          setShipByEnd(today);
          break;
        case 'todayAndTomorrow':
          setShipByEnd(tomorrow);
          break;
        case 'todayPlusTwo':
          setShipByEnd(tPlusTwoDays);
          break;
        case 'todayPlusThree':
          setShipByEnd(tPlusThreeDays);
          break;
        default:
          setShipByEnd(null);
          break;
      }
    }
  };

  const loadFilterStagingLocationsFlag = async () => {
    const response = await warehouseService.getFeatureFlag(FF_FILTER_STAGING_LOCATIONS, selectedWarehouse.id);
    if (response.errors && response.errors.length > 0) {
      setFilterStagingLocations(false);
    } else {
      setFilterStagingLocations(!!response.data.value);
    }
  };

  const loadFreightLoadGroupUIEnabledFlag = async () => {
    setIsFreightLoadGroupUIEnabled(await fetchLoadGroupUiFf());
  };

  const prevReloadShipments = usePrevious(reloadShipments);
  useEffect(() => {
    if (valueIsNotUndefined(prevReloadShipments)) {
      getShipments();
    }
  }, [reloadShipments]);

  const prevPage = usePrevious(currentPage);
  useEffect(() => {
    if (valueIsNotUndefined(prevPage)) {
      setReloadShipments(!reloadShipments);
    }
  }, [currentPage]);

  // resets page to 1, clears selections upon altering filters.
  useEffect(() => {
    resetShipmentIndexView();
  }, [searchParams, shipByEnd, shipByStart]);

  useEffect(() => {
    if (showLpnContentsModal) {
      getLpns([shipmentForLPN]);
    } else {
      setLpns(null);
    }
  }, [showLpnContentsModal]);

  const resetShipmentIndexView = () => {
    setCurrentPage(1);
    setSelectedShipments([]);
    setContinuationTokens({1: null});
    setReloadShipments(!reloadShipments);
  };

  /* For use with the new query parameter pattern (SHIPMENT-5724).
     Reset the view as a result of fetching Shipments instead of the
     other way around as it currently does. */
  const resetShipmentIndexViewWithoutReloading = () => {
    setCurrentPage(1);
    setSelectedShipments([]);
    setContinuationTokens({1: null});
  };

  const getShipments = async () => {
    setIsLoading(true);

    const token = continuationTokens[currentPage];
    const mode = searchParams.get('mode') as ShipMode;
    const sortByColumn = searchParams.get('sort_by') as SortByColumns;
    const sortDirection = searchParams.get('sort_direction') as SortOrder;

    const params: GetShipmentsParams = {
      ...filterParamsFromURLSearchParams(searchParams),
      continuation_token: token,
      mode,
      ship_by_end: shipByEnd,
      ship_by_start: shipByStart,
      sort_by: sortByColumn,
      sort_direction: sortDirection,
      status: getStatusFilters(selectedStatusFilters),
      warehouse_id: selectedWarehouse.id
    };

    const reservationId = Number(searchParams.get('reservation_id'));
    if (reservationId && reservationId !== ALL_RESERVATIONS_ID_VALUE) {
      params.reservation_id = reservationId;
    }

    try {
      const response = await outboundShipmentService.getShipmentsDeprecated(params);

      setErrors(response.errors.map((err) => err.detail));

      if (response.data === undefined || response.data.shipments === undefined) {
        setShipments([]);
        setShipmentsCount(0);
      } else {
        setShipments(response.data.shipments);
        const newContinuationTokens = {...continuationTokens};
        newContinuationTokens[currentPage + 1] = response.data.continuation_token;
        setContinuationTokens(newContinuationTokens);
        setShipmentsCount(response.data.counts);
      }
    } catch (error) {
      setErrors([error.toString()]);
      setShipments([]);
      setShipmentsCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  const getLpns = async (currentShipments: Shipment[]) => {
    const shipmentIds: number[] = [];
    if (currentShipments.length === 0) {
      return;
    }

    currentShipments.forEach((shipment) => {
      shipmentIds.push(shipment.id);
    });
    const lpnSearchRequest: LpnSearchRequest = {
      ReservationIds: [],
      ShipmentIds: shipmentIds,
      LpnBarcodes: [],
      Skus: []
    };
    const lpnSearchResponse = await lpnService.searchLpns(lpnSearchRequest);
    setLpns(lpnSearchResponse.data.lpns);
  };

  const handleWavingSuccessModalClose = () => {
    setShowWavingSuccessModal(false);
  };

  const getFilterKeys = async () => {
    const keys = [
      FilterKeys.ShipmentId,
      FilterKeys.PurchaseOrder,
      FilterKeys.Scac,
      FilterKeys.Bol,
      FilterKeys.ProNumber,
      FilterKeys.Lpn,
      FilterKeys.Sku,
      ...(isDistributionByLotEnabled ? [FilterKeys.LotCode, FilterKeys.ExpirationDate] : []),
      FilterKeys.OrderReferenceId,
      FilterKeys.DestinationTag,
      FilterKeys.CustomerReferenceNumber,
      FilterKeys.LoadId
    ];

    if (isFreightTrailerLoadingEnabled && isFreightLoadGroupUIEnabled) {
      keys.push(FilterKeys.FreightLoadGroup);
    }
    setAllowedFilterKeys(keys);
  };

  async function fetchLoadGroupUiFf() {
    const response = await warehouseService.getFeatureFlag(FF_FREIGHT_LOAD_GROUP_UI, selectedWarehouse.id);
    if (response.errors && response.errors.length > 0) {
      return false;
    } else {
      return !!response.data.value;
    }
  }

  async function callWaveByShipmentIds(shipmentIds: number[], batchCount: number, newStagingLocation: Location | null) {
    const stagingLocationId = newStagingLocation?.id;
    const batchParams: BatchWaving.WaveByShipmentIdsParams = {
      reservationId: selectedReservationId(),
      shipmentIds,
      ...(batchCount && {batchCount}),
      ...(stagingLocationId && {stagingLocationId})
    };

    const response = await batchWavingService.waveByShipmentIds(batchParams);
    setReloadAlerts(!reloadAlerts);

    if (response.errors.length > 0) {
      setErrors(response.errors.map((err) => err.detail));
      return false;
    } else {
      const failedShipments = response.data.result.failedShipments;

      if (failedShipments && failedShipments.length > 0) {
        const plural = failedShipments.length > 1;
        const shipmentErrors = [
          `Shipment ID${plural ? 's' : ''} ${failedShipments.join(', ')} ${plural ? 'are' : 'is'} not ready to wave. ` +
            'Please make sure they have the required labels and inventory and try again.'
        ];
        setErrors(shipmentErrors);
        return false;
      }
    }

    const wavingWarnings = response.data.result.warnings ?? [];
    if (wavingWarnings?.length > 0) {
      setErrors([...wavingWarnings]);
      return false;
    }

    return true;
  }

  const waveByShipmentIdsAsync = async ({stagingLocation, batchCount}: OnWaveClickOptions) => {
    const freightConsolidationEnabled = selectedShipments.some(
      (selectedShipment) => selectedShipment.isFreightConsolidationEnabled
    );
    const allShipmentsFreight = selectedShipments.every(
      (selectedShipment) => selectedShipment.shipMode === FreightShipMode
    );
    const shipmentIds = selectedShipmentIds();

    let waveRequestSucceeded = false;
    // non freight-consolidated freight waves are only one shipment per Wave, so they are safe to chunk
    if (!freightConsolidationEnabled && allShipmentsFreight) {
      const shipmentChunks = chunk(shipmentIds, 10);
      for (const shipmentChunk of shipmentChunks) {
        waveRequestSucceeded = await callWaveByShipmentIds(shipmentChunk, batchCount, stagingLocation);
        if (!waveRequestSucceeded) {
          break;
        }
      }
    } else {
      waveRequestSucceeded = await callWaveByShipmentIds(shipmentIds, batchCount, stagingLocation);
    }

    setShowWavingModal(false);

    if (waveRequestSucceeded) {
      setShowWavingSuccessModal(true);
      setReloadShipments(!reloadShipments);
      setSelectedShipments([]);
    }
  };

  const selectedReservationId = () => {
    return uniq(selectedShipments.map((selectedShipment) => selectedShipment.reservationId))[0];
  };

  const selectedShipmentIds = () => {
    return uniq(selectedShipments.map((selectedShipment) => selectedShipment.shipmentId));
  };

  const getReservations = async () => {
    const response = await warehouseService.getReservationsForWarehouse(selectedWarehouse.id);
    setReservations(response.data.reservations);
  };

  const reservationDropdown = () => {
    const options = reservations.map((reservation) => {
      return {
        name: reservation.name,
        value: `${reservation.id}`
      };
    });

    options.unshift({name: 'All', value: `${ALL_RESERVATIONS_ID_VALUE}`});

    const onSelect = (value: string) => {
      const id = searchParams.get('reservation_id');
      if (id !== value) {
        setSearchParam('reservation_id', value);
      } else {
        removeSearchParam('reservation_id');
      }
    };

    return (
      <SelectorWithCheckbox
        options={options}
        selected={[searchParams.get('reservation_id') || `${ALL_RESERVATIONS_ID_VALUE}`]}
        onSelect={onSelect}
        prefixText="Reservation: "
      />
    );
  };

  const updateFilterParams = (key: string, value: string | string[]) => {
    const hasValue = Array.isArray(value) ? value.length > 0 : !!value;

    if (searchParams.has(key) || hasValue) {
      if (hasValue) {
        setSearchParam(key, value);
      } else {
        removeSearchParam(key);
      }
    }
  };

  const errorDisplay = errors.map((error, idx) => {
    return (
      <div className="alert alert-danger" role="alert" key={`error:${idx}`}>
        {error}
      </div>
    );
  });

  const updateSelectedStatus = (sel) => {
    const filterKeys = Object.keys(sel);

    if ('all' in sel) {
      const allSelected = sel.all;

      // This handles the "Select All" checkbox. i.e. if it's clicked and now checked,
      // everything is checked. If it's clicked and not check, nothing is checked.
      if (allSelected) {
        setSelectedStatusFilters(ALL_SELECTED_STATUS_FILTERS);
      } else {
        setSelectedStatusFilters(NO_SELECTED_STATUS_FILTERS);
      }
    } else {
      const copiedFilters = cloneDeep(selectedStatusFilters);
      filterKeys.forEach((filter) => {
        copiedFilters[filter] = sel[filter];
      });
      copiedFilters.all = false;
      setSelectedStatusFilters(copiedFilters);
    }
  };

  const applyMultiStatusFilter = () => {
    const statusFilters = searchParams.getAll('status') || [];
    const newFilters = getStatusFilters(selectedStatusFilters);

    /* Check to make sure this new string[] doesn't have the same values as
       the current one. If it does it'll still trigger the useEffect that executes
       when statusFilter changes since React compares arrays by reference instead
       of their contents */
    if (isEqual(statusFilters, newFilters)) {
      return;
    }

    if (newFilters.length > 0) {
      setSearchParam('status', newFilters);
    } else {
      removeSearchParam('status');
    }
  };

  const buildColumnsToHide = () => {
    // Default columns that will always be hidden. Generally the columns were created for
    // a different consumer of this control and should always be hidden here.
    const columnsToHide: ColumnNames[] = ['destination', 'remove'];

    const allReservationsAreConfiguredToNotShowLpns = Object.values(reservationDisplayInfos).every(
      (reservationDisplayInfo: ReservationDisplayInfo) => !reservationDisplayInfo.displayLpns
    );

    if (allReservationsAreConfiguredToNotShowLpns) {
      columnsToHide.push('lpns');
    }

    columnsToHide.push(isFreightTrailerLoadingEnabled ? 'orderId' : 'loadDetails', 'pro/track');

    return columnsToHide;
  };

  const handleWavingModalClose = () => {
    setShowWavingModal(false);
  };

  const handleModeChangeAsync = async (newMode: ShipMode) => {
    if (newMode) {
      setSearchParam('mode', newMode);
    } else {
      removeSearchParam('mode');
    }
  };

  const updateShipBy = (start: Date, end: Date, option: string) => {
    if (start || end) {
      setSearchParams({
        ship_by_start: option,
        ship_by_end: option
      });
    }

    if (!start) {
      removeSearchParam('ship_by_start');
    }

    if (!end) {
      removeSearchParam('ship_by_end');
    }

    setShipByStart(start);
    setShipByEnd(end);
  };

  const updateSort = (column: SortByColumns, direction: SortOrder) => {
    setSearchParams({
      sort_by: column,
      sort_direction: direction
    });
  };

  return (
    <div className="container-fluid">
      <div id="shipment-list">
        <WavingModal
          show={showWavingModal}
          onClose={handleWavingModalClose}
          warehouseId={selectedWarehouse.id}
          selectedShipments={selectedShipments}
          onWaveClickAsync={waveByShipmentIdsAsync}
          pickConsolidationService={pickConsolidationService}
          shouldFilterStagingLocations={filterStagingLocations}
          isSfsEnabled={selectedWarehouse.isSfsEnabled}
          isFreightTrailerLoadingEnabled={isFreightTrailerLoadingEnabled}
        />
        <WavingSuccessModal
          show={showWavingSuccessModal}
          isSfsEnabled={selectedWarehouse.isSfsEnabled}
          onClose={handleWavingSuccessModalClose}
        />
        {shipmentForLPN && (
          <LpnContentsModal
            showModal={showLpnContentsModal}
            setShowModal={setShowLpnContentsModal}
            shipmentId={shipmentForLPN.id}
            lpns={lpns}
            shipmentStatus={shipmentForLPN && shipmentForLPN.status}
          />
        )}
        {isLoading && <Loader loading={isLoading} />}
        {errorDisplay}
        <div className="title-header" data-testid="shipment-filters">
          <div className="row">
            <div className="title-header-controls">
              <div className={'top-left-controls'}></div>
              <div className={'top-right-controls'}>
                <AlertWavingIssues
                  warehouseId={selectedWarehouse.id}
                  batchWavingService={batchWavingService}
                  reloadTrigger={reloadAlerts}
                />
              </div>
            </div>
          </div>
          <div className="row">
            <h1>Shipments</h1>
          </div>
          <div className="header-filter-top-row">
            <ul className="list-inline">
              <li>{reservationDropdown()}</li>
              <li>
                <ShipBy
                  onChange={updateShipBy}
                  initialValue={searchParams.get('ship_by_start') || searchParams.get('ship_by_end') || null}
                />
              </li>
            </ul>
          </div>
          <hr />
          <ShipmentFilter
            searchParams={searchParams}
            allowedFilterKeys={allowedFilterKeys}
            onFilterChange={updateFilterParams}
            outboundShipmentService={outboundShipmentService}
            warehouseId={selectedWarehouse.id}
          />
        </div>
        <div className="container-fluid">
          <div className="row space-below">
            <ModeFilter mode={(searchParams.get('mode') || null) as ShipMode} onModeChange={handleModeChangeAsync} />
            <CheckboxDropdown
              includeAllOption
              dotStyleMap={STATUS_TO_DOT_STYLE_MAP}
              optionToStatusMap={SIMPLE_SHIPMENT_STATUS_MAP}
              optionFilters={selectedStatusFilters}
              onStatusChange={updateSelectedStatus}
              applyFilters={applyMultiStatusFilter}
            />
          </div>

          <WavingControls
            shipments={shipments}
            selectedShipments={selectedShipments}
            setShowWavingModal={setShowWavingModal}
            setSelectedShipments={setSelectedShipments}
            isFreightTrailerLoadingEnabled={isFreightTrailerLoadingEnabled}
          />

          <div className="row vertical-align pull-right space-below">
            <SortBy
              onChange={updateSort}
              initialColumn={searchParams.has('sort_by') ? (searchParams.get('sort_by') as SortByColumns) : null}
              initialDirection={
                searchParams.has('sort_direction') ? (searchParams.get('sort_direction') as SortOrder) : null
              }
            />

            <PaginationButtons
              currentPage={currentPage}
              pageSize={PAGE_SIZE}
              setCurrentPage={setCurrentPage}
              shipmentsCount={shipmentsCount}
            />
          </div>

          <ShipmentTable
            shipments={shipments}
            selectedShipments={selectedShipments}
            setSelectedShipments={setSelectedShipments}
            disableSelectedShipments={false}
            isFreightTrailerLoadingEnabled={isFreightTrailerLoadingEnabled}
            setShowLpnContentsModal={setShowLpnContentsModal}
            setShipmentForLPN={setShipmentForLPN}
            columnsToHide={buildColumnsToHide()}
            showCarrierUnderPro={false}
            enableSortableHeaders={false}
            reservationDisplayInfos={reservationDisplayInfos}
            onSortChanged={(state: SortState) => {
              return;
            }}
            onRemoveShipment={(shipment: Shipment) => {
              return;
            }}
            isFreightLoadGroupUIEnabled={isFreightLoadGroupUIEnabled}
          />
        </div>
      </div>
    </div>
  );
}
export default ShipmentIndex;
